import './SelectTeamPagesModal.pcss';
import React, { useEffect, useRef, useState } from 'react';
import { includes, without } from 'lodash';
import { Dispatch } from 'redux';
import { compose } from 'recompose';
import { connect, useDispatch, useSelector } from 'react-redux';
import BasicModalTemplate from '../../../../../common/modals/BasicModalTemplate';
import { ModalInstanceProps } from '../../../../../common/modals/interfaces';
import { LogoAndText } from '../../common/LogoAndText/LogoAndText';
import { StoreState } from '@mm/state/store-interfaces';
import { OpenSelectTeamPagesModalParams } from '@mm/state/modules/modalsState/actions';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PageSelectItem } from './PageSelectItem/PageSelectItem';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { ModalProcessingBlinder } from '../../../../../common/modals/modal-helpers';
import { enhancedBillingPagesSelector } from '@mm/state/selectors/facebookPageSelector';
import { centsToDollars } from '@mm/utils';
import { SearchInput } from '../../../../../common/components/SearchInput/SearchInput';
import { useDebouncedFetchSearchQuery } from '../../../../../common/hooks/use-debounced-fetch-search-query';
import { getPlanPrice } from '@mm/state/helpers/get-plan-price';
import { Routes } from '../../../../../routes/top-level-routes';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import useRouter from 'use-react-router';

type OuterProps = OpenSelectTeamPagesModalParams & ModalInstanceProps;

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const useSelectedPages = (props: Props) => {
  const { facebookPages, subscription } = props;
  const [selectedPages, setSelectedPages] = useState<number[]>([]);

  useEffect(() => {
    const currentSelectedPages = facebookPages.filter(page => {
      return page.subscription && page.subscription.id === subscription.id;
    });
    const currentSelectedPagesIds = currentSelectedPages.map(page => page.id);

    setSelectedPages(currentSelectedPagesIds);
  }, []);

  return { selectedPages, setSelectedPages };
};

const useResetPageResultsOnClose = (searchInput: string) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const company = useSelector((state: StoreState) =>
    activeCompanySelector(state),
  );
  const searchInputRef = useRef(searchInput);
  searchInputRef.current = searchInput;

  useEffect(() => {
    return () => {
      if (searchInputRef.current && company) {
        dispatch(
          connectedPageCRUDActions.retrieveBasicListByCompany(
            company.id,
            { withFacebookErrors: true, withSubscription: true },
            true,
          ),
        );
      }
    };
  }, []);
};

const SelectTeamPagesModal = (props: Props) => {
  const { facebookPages, company, attachPagesTeam, subscription, closeModal } =
    props;
  const { selectedPages, setSelectedPages } = useSelectedPages(props);
  const [isProcessing, setIsProcessing] = useState(false);
  const pagesLimit = subscription.pagesLimit ? subscription.pagesLimit : 10;
  const metMaximumPages = selectedPages.length === pagesLimit;
  const [searchInput, setSearchInput] = useState('');
  const debouncedFetchSearchQuery = useDebouncedFetchSearchQuery(350);
  useResetPageResultsOnClose(searchInput);
  const activeChatbot = useSelector(
    (state: StoreState) => state.chatbot.activeChatbot,
  );
  const router = useRouter();

  const pageIsAlreadySelected = (page: EnhancedBillingPage) =>
    !!page.subscription && page.subscription.id === subscription.id;

  const notSelected = facebookPages.filter(p => !pageIsAlreadySelected(p));

  const facebookPagesToShow = [
    ...facebookPages.filter(p => pageIsAlreadySelected(p)),
    ...notSelected,
  ];

  const proRatedCredit = notSelected
    .filter(p => ~(selectedPages || []).indexOf(p.id))
    .reduce((sum, page) => {
      if (page && page.subscription) {
        const price = getPlanPrice(
          page.subscription.unitAmountInCents,
          page.subscription.coupon,
        );
        const credit = page.subscription.priceFactor * price;
        return sum + credit;
      }
      return sum;
    }, 0);

  const onPageSelect = (page: EnhancedBillingPage) => {
    const isSelected = includes(selectedPages, page.id);

    if (isSelected) {
      const updatedList = without(selectedPages, page.id);
      setSelectedPages(updatedList);
    } else {
      setSelectedPages([...selectedPages, page.id]);
    }
  };

  const navigateToDashboard = () => {
    let backLink = Routes.chatbotEditor.getLink();
    if (activeChatbot && activeChatbot.companyId) {
      backLink = BotBuilderRoutes.dashboard.getLink({
        chatbotId: activeChatbot.id,
      });
    }
    router.history.push(backLink);
  };

  const attachPages = async () => {
    if (company) {
      setIsProcessing(true);
      await attachPagesTeam(company.id, subscription.id, selectedPages);
      closeModal();
      navigateToDashboard();
    }
  };

  const onSearchInputChange = (searchInput: string) => {
    setSearchInput(searchInput);

    if (company) {
      debouncedFetchSearchQuery(company.id, searchInput);
    }
  };

  return (
    <BasicModalTemplate
      className="select-team-pages-modal"
      withMonkeyBackground={false}
    >
      <div className="select-team-pages-modal__choose-text">
        Choose up to {pagesLimit} pages
      </div>

      <div className="select-team-pages-modal__totals-wrapper">
        <div className="select-team-pages-modal__totals-section">
          <span className="select-team-pages-modal__big-number">
            {selectedPages.length}
          </span>
          <span>selected</span>
        </div>

        <div className="select-team-pages-modal__totals-section">
          <span className="select-team-pages-modal__big-number">
            {pagesLimit - selectedPages.length}
          </span>
          <span>remaining</span>
        </div>

        <SearchInput
          className="select-team-pages-modal__search-icon"
          hasBorder={true}
          placeholder="search pages"
          onChange={event => onSearchInputChange(event.target.value)}
          value={searchInput}
        />
      </div>

      {proRatedCredit > 0 && (
        <div className="select-team-pages-modal__credit-info">
          Credit for future payments: ${centsToDollars(proRatedCredit)}
        </div>
      )}

      {!facebookPagesToShow.length && searchInput ? (
        <div className="select-team-pages-modal__no-results">
          No pages match search: {searchInput}
        </div>
      ) : (
        <div className="select-team-pages-modal__pages-list">
          {facebookPagesToShow.map(page => (
            <PageSelectItem
              key={page.id}
              metMaximumPages={metMaximumPages}
              page={page}
              onClick={onPageSelect}
              selectedPages={selectedPages}
              forceSelection={pageIsAlreadySelected(page)}
            />
          ))}
        </div>
      )}

      <PrimaryButton
        onClick={attachPages}
        disabled={!selectedPages.length}
        className="select-team-pages-modal__confirm-button"
      >
        Confirm
      </PrimaryButton>

      <ModalProcessingBlinder
        show={isProcessing}
        className="select-team-pages-modal__blinder"
      />
    </BasicModalTemplate>
  );
};

const mapStateToProps = (state: StoreState) => ({
  facebookPages: enhancedBillingPagesSelector(state),
  company: activeCompanySelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  attachPagesTeam: (companyId: number, subscriptionId: number, ids: number[]) =>
    dispatch(
      connectedPageCRUDActions.attachPagesTeam(companyId, subscriptionId, ids),
    ),
});

const enhance = compose<OuterProps, InnerProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(SelectTeamPagesModal);
